import FilterDrawer from 'components/Assignments/FilterDrawer';
import PullAssignmentDialog from 'components/InstructorAssignments/PullAssignmentDialog';
import { features } from 'config/features';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import Desktop from 'pages/InstructorAssignments/desktop';
import Mobile from 'pages/InstructorAssignments/mobile';
import { PageContext } from 'pages/InstructorAssignments/PageProvider';
import Tablet from 'pages/InstructorAssignments/tablet';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { Button, Loading, Stack, Text } from 'shell/components';
import { buildAssignmentVisibleRows } from 'utils/chat/instructorHugoContext';
import { CITATION_SELECT_OPTIONS } from 'utils/constants';
import { isAssignmentValid } from 'utils/helper';
import { convertCanvasRubric, convertD2LRubric, normalizeRubricScoringMode } from 'utils/rubricScoring';
import { v4 as uuid } from 'uuid';

const PageLayout = () => {
    const { isLoading, course, assignments, setAssignments, setInstructorHugoVisibleRows } = useApp();
    const { auth, currentUser } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { joyrideRun, setJoyrideRun, setJoyridePaused, joyrideStepIndex, setJoyrideStepIndex, performNext } =
        useJoyride();
    const { message, close } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const {
        loading,
        filters,
        setFilters,
        resetFilters,
        getAPIAssignments,
        fetchedAssignments,
        pullAssignmentDialog,
        setPullAssignmentDialog,
    } = useContext(PageContext);

    const [isDuplicating, setIsDuplicating] = useState(false);

    const visibleAssignments = useMemo(() => {
        return (assignments || [])
            .filter(({ title }) => title.toLowerCase().includes(filters.search.toLowerCase()))
            .sort((a, b) => {
                const aTitle = a.title.toLowerCase();
                const bTitle = b.title.toLowerCase();
                if (filters.sort === 'titlea-z') {
                    if (aTitle < bTitle) return -1;
                    if (aTitle > bTitle) return 1;
                }
                if (filters.sort === 'titlez-a') {
                    if (aTitle < bTitle) return 1;
                    if (aTitle > bTitle) return -1;
                }
                return 0;
            });
    }, [assignments, filters.search, filters.sort]);

    useEffect(() => {
        setInstructorHugoVisibleRows(buildAssignmentVisibleRows(visibleAssignments));
        return () => setInstructorHugoVisibleRows(null);
    }, [setInstructorHugoVisibleRows, visibleAssignments]);

    const getFilterCount = (filters) => {
        let count = 0;
        if (filters.sort !== 'none') count++;
        return count;
    };

    const onClickCreate = () => {
        if (joyrideRun) return performNext();
        history.push('/assignments/new');
    };

    const onClick = ({ id }) => {
        if (joyrideRun) {
            if (joyrideStepIndex === 11) {
                history.push(`/assignments/${id}`);
                setJoyrideRun(false);
                return;
            }
            return performNext();
        }
        history.push(`/assignments/${id}`);
    };

    const onClickEdit = ({ id }) => {
        history.push(`/assignments/${id}/edit`);
    };

    const onClickDuplicate = async (assignment) => {
        setIsDuplicating(true);
        const timestamp = new Date().getTime();
        const duplicate = {
            ...omit(assignment, [
                '_id',
                'api_assignment_id',
                'api_data',
                'batchCount',
                'submissionCount',
                'is_rag_ready',
                'generated_id',
            ]),
            id: uuid(),
            owner_account_id: auth.id,
            created_on: timestamp,
            modified_on: timestamp,
            title: t('duplicate_title', 'Copy of {{title}}', { title: assignment.title }),
            rubric: normalizeRubricScoringMode(assignment),
            is_draft: true,
            has_seen_preview: false,
            has_finished_preview: !features.enableFinetuning, // initialize as true when fine tuning is hidden
            preview_files: [],
        };
        const createdDuplicate = await courseService.createCourseAssignment(course.id, duplicate);
        setAssignments((current) => [...current, createdDuplicate]);
        message.success(
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Text color={'inherit'} variant={'p1'}>
                    {t('message.duplicate-success', 'Your assignment was successfully duplicated.')}
                </Text>
                <Button
                    color={'neutral'}
                    size={'large'}
                    variant={'borderless'}
                    onClick={() => {
                        history.push(`/assignments/${createdDuplicate.id}`);
                        close();
                    }}
                >
                    {t('common:controls.open', 'Open')}
                </Button>
            </Stack>
        );
        setIsDuplicating(false);
    };

    const onClickUnpublish = (assignment) => {
        setDialog({
            open: true,
            message: t('dialog.unpublish-assignment.title', 'Unpublish assignment?'),
            description: t(
                'dialog.unpublish-assignment.description',
                'Are you sure you want to unpublish this assignment?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.unpublish', 'Unpublish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: true,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: true,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickPublish = (assignment) => {
        if (!isAssignmentValid(assignment)) {
            return setDialog({
                open: true,
                message: t('dialog.required-information-incomplete.title', 'Required Information Incomplete'),
                description: t(
                    'dialog.required-information-incomplete.description',
                    'Before you can publish this assignment, please edit it and add any missing required information.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        setDialog({
            open: true,
            message: t('dialog.publish-assignment.title', 'Publish assignment?'),
            description: t(
                'dialog.publish-assignment.description',
                'Are you sure you want to publish this assignment?'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.publish', 'Publish'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: false,
                        };
                    });
                });
                closeDialog();
                message.success(t('message.update-success', 'Your assignment was successfully updated.'));
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: false,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickDelete = (assignment) => {
        setDialog({
            open: true,
            message: t('dialog.delete-assignment.title', 'Delete Assignment?'),
            description: t('dialog.delete-assignment.description', 'This action cannot be undone.'),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setAssignments((current) => current.filter(({ id }) => id !== assignment.id));
                closeDialog();
                message.success(t('message.delete-success', 'Your assignment was successfully deleted.'));
                await assignmentService.deleteAssignment(assignment.id);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleSelectAssignment = (assignment) => {
        if (process.env.REACT_APP_KIND === 'D2L') {
            const rubric = assignment.Assessment?.Rubrics?.[0];
            const convertAssignment = {
                api_assignment_id: assignment.Id,
                api_data: assignment,
                title: assignment.Name,
                description: assignment.CustomInstructions.Html,
                dueDate: assignment.DueDate,
                citationFormat: CITATION_SELECT_OPTIONS[0].key,
                contentBankResources: [],
                rubric: rubric ? convertD2LRubric(rubric) : null,
            };
            return history.push({
                pathname: `/assignments/new`,
                state: { assignment: convertAssignment },
            });
        } else {
            const convertAssignment = {
                api_assignment_id: assignment.id,
                title: assignment.name,
                description: assignment.description,
                dueDate: assignment.due_at,
                citationFormat: CITATION_SELECT_OPTIONS[0].key,
                contentBankResources: [],
                rubric: assignment.rubric ? convertCanvasRubric(assignment.rubric, assignment.rubric_settings) : null,
                api_data: assignment,
            };
            return history.push({
                pathname: `/assignments/new`,
                state: { assignment: convertAssignment },
            });
        }
    };

    useEffect(() => {
        if (currentUser?.finished_onboarding || isLoading) return;
        if (joyrideStepIndex < 2) {
            setJoyrideStepIndex(2);
            setJoyrideRun(true);
        }
    }, [currentUser?.finished_onboarding, isLoading, joyrideStepIndex, setJoyrideRun, setJoyrideStepIndex]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    const props = {
        loading,
        isDuplicating,
        filters,
        setFilters,
        getFilterCount,
        getAPIAssignments,
        setPullAssignmentDialog,
        onClickCreate,
        onClick,
        onClickEdit,
        onClickDuplicate,
        onClickUnpublish,
        onClickPublish,
        onClickDelete,
        assignments: visibleAssignments,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <PullAssignmentDialog
                open={pullAssignmentDialog.open}
                assignments={assignments}
                fetchedAssignments={fetchedAssignments}
                onClose={() => setPullAssignmentDialog({ open: false })}
                onSave={handleSelectAssignment}
            />
            <FilterDrawer filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </>
    );
};

export default PageLayout;
